import { isBackupKey, type LocalBackup } from './local-backup';

type RestoreResult = { inserted: number; unchanged: number; conflicts: number };
const stores: Record<string, ReadonlySet<string>> = {
  'yumnote-web': new Set(['meals', 'meal_items', 'water_entries', 'sync_operations']),
  'yumnote-statistics': new Set(['reports']),
};
function object(value: unknown): value is Record<string, unknown> { return !!value && typeof value === 'object' && !Array.isArray(value); }
/** Reject unknown data before any writes. No stored value is ever overwritten. */
export function validateBackup(value: unknown): asserts value is LocalBackup {
  if (!object(value) || value.format !== 'yumnote-local-backup' || value.version !== 1 || typeof value.createdAt !== 'string' || !object(value.localStorage) || !object(value.databases)) throw new Error('Неверный формат резервной копии.');
  for (const [key, data] of Object.entries(value.localStorage)) if (!isBackupKey(key) || typeof data !== 'string') throw new Error('Резервная копия содержит неизвестные данные.');
  for (const [name, database] of Object.entries(value.databases)) {
    const allowed = Object.hasOwn(stores, name) ? stores[name] : undefined;
    if (!allowed || !object(database)) throw new Error('Резервная копия содержит неизвестную базу.');
    for (const [store, entries] of Object.entries(database)) {
      if (!allowed.has(store) || !Array.isArray(entries) || entries.some(entry => !object(entry) || typeof entry.id !== 'string' || !entry.id)) throw new Error('Резервная копия содержит повреждённые записи.');
      if (new Set(entries.map(entry => (entry as { id: string }).id)).size !== entries.length) throw new Error('В резервной копии повторяются записи.');
    }
  }
}
function openExisting(name: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let missing = false; const req = indexedDB.open(name);
    req.onupgradeneeded = () => { missing = true; req.transaction?.abort(); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(new Error(missing ? 'Сначала откройте соответствующий раздел приложения.' : 'Не удалось открыть локальную базу.'));
    req.onblocked = () => reject(new Error('Другая вкладка блокирует локальную базу.'));
  });
}
async function restoreDatabase(name: string, data: Record<string, unknown[]>, result: RestoreResult): Promise<void> {
  const db = await openExisting(name);
  try {
    const names = Object.keys(data).filter(key => data[key]!.length > 0);
    if (!names.length) return;
    if (names.some(key => !db.objectStoreNames.contains(key))) throw new Error('Версия локальной базы не поддерживает восстановление.');
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(names, 'readwrite');
      for (const name of names) {
        const store = tx.objectStore(name);
        for (const entry of data[name]!) {
          const id = (entry as { id: string }).id; const get = store.get(id);
          get.onsuccess = () => {
            if (get.result === undefined) { store.add(entry); result.inserted++; }
            else if (JSON.stringify(get.result) === JSON.stringify(entry)) result.unchanged++;
            else result.conflicts++;
          };
        }
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Не удалось восстановить записи.'));
      tx.onabort = () => reject(tx.error ?? new Error('Восстановление прервано.'));
    });
  } finally { db.close(); }
}
export async function restoreMissingFromBackup(input: unknown): Promise<RestoreResult> {
  validateBackup(input);
  const result: RestoreResult = { inserted: 0, unchanged: 0, conflicts: 0 };
  // Restoring lists, profiles or recipes could overwrite a different device's state;
  // only fill a missing key, and retain any existing version unchanged.
  for (const [key, value] of Object.entries(input.localStorage)) {
    const existing = localStorage.getItem(key);
    if (existing === null) { localStorage.setItem(key, value); result.inserted++; }
    else if (existing === value) result.unchanged++;
    else result.conflicts++;
  }
  for (const [name, data] of Object.entries(input.databases)) await restoreDatabase(name, data, result);
  return result;
}
